import Semaphore from "semaphore-async-await"
import { createAction } from "@reduxjs/toolkit"
import { wait } from "@nova/promise-wait"

//#region custom error types
export class ChangedHookError extends Error {
    constructor(message: string) {
        super(`@nova/slice-multifile-upload -- hook changed in processing, ${message}`)
    }
}
//#endregion

//#region events
//
// events are used to communicate changes to the store to the processing function
// this is a global instance so it is shared by all processing functions, so event names should include specific ids
const events = new EventTarget()

//
// the actual dispatch function - should be used outside the hooks
export const dispatch = (eventname: string) => {
    events.dispatchEvent(new Event(eventname))
}

//
// helpers for event names
export const eventnames = (name: string) => ({
    deleteInstance: (id: string) => `${name}:deleted:${id}`,
    signalSet: (signal: string, setid: string) => `${name}:signal:${signal}:${setid}`,
})
//#endregion

//#region checkEqual
//
// iterates over two arrays and ensures they are equivalent
export const checkEqual = (before: Array<unknown>, after: Array<unknown>) => {
    if (before.length != after.length) {
        throw new ChangedHookError("length mismatch")
    }
    for (let i = 0; i < before.length; i++) {
        if (before[i] != after[i]) {
            throw new ChangedHookError(
                `difference at ${i} - was ${JSON.stringify(before[i])}, now ${JSON.stringify(
                    after[i]
                )}`
            )
        }
    }
}
//#endregion

//
// HookActions are actions that hooks can take on state
type HookActions = {
    addPending: (name: string, setid: string) => void
    removePending: (name: string, setid: string) => void
}

//
// HookState captures the state of a hook
type HookState = {
    // guards are any value checks that need to happen - if these change, then something has gone wrong
    guards: unknown[]
    // the actual value stored in the hook state
    instance: unknown
}

export function createHooks(slicename: string) {
    // the hook state is shared between all functions that use the hooks created by this
    // this means that if two functions both call 'useState' then they'll get the same value, unboxed from this array
    const hookState: Array<HookState> = []

    // useState is the building block for the other hooks we expose, but isn't exposed directly
    const createUseState = () => {
        // each instance of processing gets their own index into the array of hook states
        // this differs from some of the other hook implementations which externalize it
        // the reason for this is so that we can use hooks asynchronously - the `currentHook` value is tied to one async function
        let currentHook = 0

        return <T>(createInstance: () => T, guards: unknown[]): T => {
            let hook = hookState[currentHook]
            // hook is undefined, so we're the first instance to hit it
            if (hook === undefined) {
                // create a new instance, capturing the guards
                hook = hookState[currentHook] = {
                    instance: createInstance(),
                    guards,
                }
            } else {
                // hook is not undefined, so verify the guards haven't changed
                // this captures the situation like this:
                // if (one) { useState(...) } else { useState(...) }
                // and ensures we don't 'share' the state between the two by erroring out
                // NOTE - always use hooks in the root without questions (same as react hooks)
                checkEqual(guards, hook.guards)
            }
            // increment this threads hook
            currentHook++
            return hook.instance as T
        }
    }
    const signal = createAction(`${slicename}/signal`, (signalName: string, setId: string) => {
        dispatch(eventnames(slicename).signalSet(signalName, setId))
        return { payload: { signalName, setId } }
    })

    function hooks(id: string, { addPending, removePending }: HookActions) {
        const controller = new AbortController()
        const signal = controller.signal

        //
        // setup delete handler
        const deleteHandler = () => {
            controller.abort()
        }
        const deleteEvent = eventnames(slicename).deleteInstance(id)
        events.addEventListener(deleteEvent, deleteHandler)

        //
        // create hook state for this batch of hooks
        const useState = createUseState()

        //
        // useSemaphore ensures only `count` instances are in the protected block
        async function useSemaphore(
            count: number,
            protectedBlock: () => Promise<void>
        ): Promise<void> {
            // useMax is one of the guards which ensures we don't
            const semaphore = useState(() => new Semaphore(count), ["useMax", count])
            await semaphore.acquire()
            try {
                if (signal?.aborted) {
                    throw new Error("@nova/slice-multifile-upload -- aborted useSemaphore")
                } else {
                    await protectedBlock()
                }
            } finally {
                semaphore.release()
            }
        }

        //
        // use signal waits until we get a specific signal
        async function useSignal(name: string, setid: string): Promise<void> {
            const eventname = eventnames(slicename).signalSet(name, setid)

            addPending(name, setid)
            try {
                await new Promise<void>((resolve, reject) => {
                    const done = () => {
                        signal?.removeEventListener("abort", abortHandler)
                        events.removeEventListener(eventname, eventHandler)
                    }
                    const abortHandler = () => {
                        done()
                        reject(new Error("@nova/slice-multifile-upload -- aborted event listener"))
                    }
                    const eventHandler = () => {
                        done()
                        resolve()
                    }
                    signal?.addEventListener("abort", abortHandler)
                    events.addEventListener(eventname, eventHandler)
                })
            } finally {
                removePending(name, setid)
            }
        }

        //
        // use wait just waits for a fixed time - useful for testing
        async function useWait(time: number): Promise<void> {
            await wait(time)
            if (signal?.aborted) {
                throw new Error("@nova/slice-multifile-upload -- aborted useWait")
            }
        }

        return {
            useSemaphore,
            useSignal,
            useWait,
        }
    }

    return {
        signal,
        hooks,
    }
}
